/**
 * AuthState — Single Source of Truth for auth/profile bootstrapping
 * ==================================================================
 * Eski sistemde 5 boolean (user, profile, profileLoading, sessionChecked,
 * profileLoadError) ve 3 ayrı redirect noktası vardı. Race condition'lar
 * "profile null + henüz fetch edilmedi" durumunu "profil yok" gibi
 * yorumlayıp kullanıcıyı yanlış sayfalara fırlatıyordu.
 *
 * Bu modül auth/profile lifecycle'ını **tek state machine**'le modeller.
 * Tek redirect kararı `<AuthGate>` component'inde, state'e göre verilir.
 *
 * State graph:
 *
 *   BOOTING ──► CHECKING_SESSION
 *               │
 *               ├─► NO_SESSION         (anon — public/login route'lar render)
 *               │
 *               └─► LOADING_PROFILE
 *                   │
 *                   ├─► LOADING_FIRMA          (profil OK → firma listesi fetch)
 *                   │   │
 *                   │   └─► READY              (profil + firma context hazır — auth-route'tan
 *                   │                            doğru hedefe yönlendirilir)
 *                   ├─► NEEDS_PROFILE          (404 PROFILE_NOT_FOUND — gerçek kayıt yok)
 *                   ├─► ACCOUNT_SUSPENDED      (aktifMi === false)
 *                   └─► TRANSIENT_ERR          (429/5xx/network — retry edilir)
 *
 * Geçişler `useAuthBootstrap` hook'unun içinde olur, pure fonksiyonlar
 * (reducer + decideTarget). Test edilebilir, UI'dan bağımsız.
 */

export type AuthState =
  | 'BOOTING'
  | 'CHECKING_SESSION'
  | 'NO_SESSION'
  | 'LOADING_PROFILE'
  | 'LOADING_FIRMA'
  | 'READY'
  | 'NEEDS_PROFILE'
  | 'ACCOUNT_SUSPENDED'
  | 'TRANSIENT_ERR';

export interface AuthProfile {
  id: string;
  email: string;
  isim?: string | null;
  soyisim?: string | null;
  unvan?: string | null;
  hesapTuru?: string | null;
  rol?: string | null;
  /** Hesap aktiflik durumu — Prisma Profil.aktifMi. SUSPENDED/INACTIVE/DELETED state için tek kaynak. */
  aktifMi?: boolean;
  /** Subscription/kredi bilgisi — yeni schema (krediBakiyesi vs.) */
  [key: string]: unknown;
}

export interface AuthError {
  code: string;
  message: string;
  hint?: string;
  status?: number;
  /** Geçici hata mı (auto-retry edilir)? */
  isTransient: boolean;
}

export interface AuthSnapshot {
  state: AuthState;
  /** Supabase auth user (oturum varsa) */
  userId: string | null;
  email: string | null;
  /** Yüklenmiş profil (READY iken set) */
  profile: AuthProfile | null;
  /**
   * Kullanıcının firma sayısı. READY iken kesin doğru (LOADING_FIRMA'da fetch edildi).
   * Firma fetch başarısızlığında 0'a düşer → resolver güvenli default ile onboarding'e
   * yönlendirir (kullanıcı yerinden çıkmasın, manuel firma oluşturma her zaman mümkün).
   */
  firmaCount: number;
  /** Tek firma senaryosu için ID — SingleFirmaStrategy openFirma() çağırır */
  singleFirmaId: string | null;
  /** Son hata (TRANSIENT_ERR / NEEDS_PROFILE / ACCOUNT_SUSPENDED iken set) */
  error: AuthError | null;
  /** Retry sayısı (transient hata sırasında artar, başarıda sıfırlanır) */
  retryCount: number;
  /** State'in son güncellenme zamanı (debug + cache) */
  updatedAt: number;
}

export const INITIAL_SNAPSHOT: AuthSnapshot = {
  state: 'BOOTING',
  userId: null,
  email: null,
  profile: null,
  firmaCount: 0,
  singleFirmaId: null,
  error: null,
  retryCount: 0,
  updatedAt: Date.now()
};

// ─── Backend error code → AuthError mapping ──────────────────────────────────

/**
 * Backend response payload'ını (veya axios error'unu) AuthError'a normalize et.
 *
 * Tek sözleşme:
 *   - 404 + code='PROFILE_NOT_FOUND' → NEEDS_PROFILE (kalıcı, retry yok)
 *   - 403 + suspended/inactive       → ACCOUNT_SUSPENDED (kalıcı)
 *   - 401                            → token invalid (logout tetikler — hook handle eder)
 *   - 429 / 500-599 / network        → TRANSIENT_ERR (retry edilir)
 *   - diğer 4xx                      → TRANSIENT_ERR (defensive — kullanıcıyı yerinden çıkarma)
 */
export function classifyError(err: unknown): AuthError {
  // axios-like error
  type AxiosLike = {
    response?: { status?: number; data?: { code?: string; message?: string; hint?: string } };
    message?: string;
    code?: string;
  };
  const e = err as AxiosLike;
  const status = e?.response?.status;
  const payloadCode = e?.response?.data?.code;
  const payloadMessage = e?.response?.data?.message;
  const payloadHint = e?.response?.data?.hint;

  // Backend PROFILE_NOT_FOUND — kalıcı, kullanıcı kayıt akışına gitmeli
  if (status === 404 && payloadCode === 'PROFILE_NOT_FOUND') {
    return {
      code: 'PROFILE_NOT_FOUND',
      message: payloadMessage || 'Profil kaydınız bulunamadı.',
      hint: payloadHint,
      status,
      isTransient: false
    };
  }

  // Backend ACCOUNT_SUSPENDED — backend defense-in-depth gate'i.
  // Frontend useAuthBootstrap zaten profile.aktifMi check ediyor ama
  // suspended user başka API endpoint'e (frontend bypass'ı vb.) direkt
  // istek atarsa 403 burada yakalanır → state ACCOUNT_SUSPENDED.
  if (status === 403 && payloadCode === 'ACCOUNT_SUSPENDED') {
    return {
      code: 'ACCOUNT_SUSPENDED',
      message: payloadMessage || 'Hesabınız aktif değil.',
      hint: payloadHint,
      status,
      isTransient: false
    };
  }

  // Eski 403 fallback (henüz migrate edilmemiş backend için geriye uyum)
  if (status === 403 && (payloadCode === 'PROFILE_NOT_FOUND' || !payloadCode)) {
    return {
      code: 'PROFILE_NOT_FOUND',
      message: payloadMessage || 'Profil kaydınız bulunamadı.',
      hint: payloadHint,
      status,
      isTransient: false
    };
  }

  // 401 — token invalid (caller logout tetikler)
  if (status === 401) {
    return {
      code: 'UNAUTHENTICATED',
      message: 'Oturum süresi doldu.',
      status,
      isTransient: false
    };
  }

  // 429 — rate limit (geçici)
  if (status === 429) {
    return {
      code: 'RATE_LIMITED',
      message: 'Çok sık istek gönderildi.',
      hint: 'Birkaç saniye bekleyin, otomatik yenilenecek.',
      status,
      isTransient: true
    };
  }

  // 5xx — server hatası (geçici)
  if (status && status >= 500) {
    return {
      code: payloadCode || 'SERVER_ERROR',
      message: payloadMessage || 'Sunucu yanıt vermiyor.',
      hint: payloadHint || 'Birkaç saniye sonra tekrar denenecek.',
      status,
      isTransient: true
    };
  }

  // Network error (no response)
  if (!status) {
    return {
      code: 'NETWORK_ERROR',
      message: 'Bağlantı sorunu.',
      hint: 'İnternet bağlantınızı kontrol edin.',
      isTransient: true
    };
  }

  // Diğer 4xx — defensive: transient say, kullanıcıyı yerinden çıkarma
  return {
    code: payloadCode || `HTTP_${status}`,
    message: payloadMessage || `Beklenmedik yanıt (${status}).`,
    hint: payloadHint,
    status,
    isTransient: true
  };
}

// ─── Backoff helpers ─────────────────────────────────────────────────────────

/** Exponential backoff: 500ms, 1500ms, 4500ms — toplam ~6 sn 3 retry */
export function backoffMs(retryCount: number): number {
  const base = 500;
  const factor = 3;
  return Math.min(base * Math.pow(factor, retryCount), 5000);
}

export const MAX_RETRIES = 3;

// ─── State transition helpers ────────────────────────────────────────────────

export function snapshotWith(prev: AuthSnapshot, patch: Partial<AuthSnapshot>): AuthSnapshot {
  return { ...prev, ...patch, updatedAt: Date.now() };
}

/** "Loading'imsi" state — UI loader göstermeli */
export function isLoadingState(s: AuthState): boolean {
  return s === 'BOOTING' || s === 'CHECKING_SESSION' || s === 'LOADING_PROFILE' || s === 'LOADING_FIRMA';
}

/** "Profile var" state — protected route'lar render edilebilir */
export function isReadyState(s: AuthState): boolean {
  return s === 'READY';
}

/** "Anonim" state — public route'lar render edilebilir */
export function isAnonState(s: AuthState): boolean {
  return s === 'NO_SESSION';
}

/** "Geçici hata" state — toast göster, sayfada kal, retry sürer */
export function isTransientErrorState(s: AuthState): boolean {
  return s === 'TRANSIENT_ERR';
}
